import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Switch,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Alert,
  Tooltip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useConditionStore } from '../store/conditionStore';
import ConditionEditor from '../components/ConditionEditor';
import AiParseInput from '../components/AiParseInput';
import { CATEGORY_LABELS, RULE_TYPE_LABELS } from '../utils/constants';
import {
  fetchAutoTradeStatus,
  toggleAutoTrade,
  runAutoTrade,
  updateAutoTradeConfig,
} from '../api/autoTrade';
import type { ConditionRule, ConditionCategory, AutoTradeStatus } from '../types';

/**
 * 条件管理页
 * Tab(买入/卖出/整体) + 条件列表 + AI解析输入框 + 编辑器
 */
export default function ConditionsPage() {
  const {
    conditions,
    activeCategory,
    loading,
    fetchConditions,
    setActiveCategory,
    createCondition,
    updateCondition,
    deleteCondition,
    resetDefaults,
    evaluate,
    evaluating,
    candidates,
  } = useConditionStore();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingCondition, setEditingCondition] = useState<ConditionRule | null>(null);

  // Auto-trade state
  const [autoTradeStatus, setAutoTradeStatus] = useState<AutoTradeStatus | null>(null);
  const [autoTradeLoading, setAutoTradeLoading] = useState(false);
  const [autoTradeResult, setAutoTradeResult] = useState<string | null>(null);
  const [maxStocksInput, setMaxStocksInput] = useState('10');

  useEffect(() => {
    fetchConditions();
  }, [fetchConditions]);

  useEffect(() => {
    loadAutoTradeStatus();
  }, []);

  const loadAutoTradeStatus = async () => {
    try {
      const status = await fetchAutoTradeStatus();
      setAutoTradeStatus(status);
      setMaxStocksInput(String(status.maxStocks));
    } catch (e) {
      console.error('Failed to load auto-trade status:', e);
    }
  };

  const handleToggleAutoTrade = async () => {
    if (!autoTradeStatus) return;
    setAutoTradeLoading(true);
    try {
      const status = await toggleAutoTrade(!autoTradeStatus.enabled);
      setAutoTradeStatus(status);
      setAutoTradeResult(`自动交易已${status.enabled ? '开启' : '关闭'}`);
      setTimeout(() => setAutoTradeResult(null), 3000);
    } catch (e: any) {
      setAutoTradeResult(`操作失败: ${e.message}`);
    } finally {
      setAutoTradeLoading(false);
    }
  };

  const handleRunAutoTrade = async () => {
    setAutoTradeLoading(true);
    try {
      const result = await runAutoTrade();
      if (result.paused) {
        setAutoTradeResult(`自动交易暂停：${result.reason}`);
      } else {
        setAutoTradeResult(`自动交易完成：买入${result.bought}只，卖出${result.sold}只`);
      }
      setTimeout(() => setAutoTradeResult(null), 5000);
      loadAutoTradeStatus();
    } catch (e: any) {
      setAutoTradeResult(`执行失败: ${e.message}`);
    } finally {
      setAutoTradeLoading(false);
    }
  };

  const handleSaveMaxStocks = async () => {
    const n = parseInt(maxStocksInput, 10);
    if (isNaN(n) || n < 1 || n > 50) {
      setAutoTradeResult('每只最大持仓数应在 1-50 之间');
      return;
    }
    try {
      const status = await updateAutoTradeConfig({ maxStocks: n });
      setAutoTradeStatus(status);
      setAutoTradeResult('配置已更新');
      setTimeout(() => setAutoTradeResult(null), 2000);
    } catch (e: any) {
      setAutoTradeResult(`更新失败: ${e.message}`);
    }
  };

  const handleAdd = () => {
    setEditingCondition(null);
    setEditorOpen(true);
  };

  const handleEdit = (condition: ConditionRule) => {
    setEditingCondition(condition);
    setEditorOpen(true);
  };

  const handleSave = async (condition: Partial<ConditionRule>) => {
    if (condition.id) {
      await updateCondition(condition.id, condition);
    } else {
      await createCondition(condition);
    }
  };

  const handleDelete = async (id: number) => {
    if (confirm('确认删除此条件？')) {
      await deleteCondition(id);
    }
  };

  const handleToggleEnabled = async (condition: ConditionRule) => {
    await updateCondition(condition.id!, { enabled: !condition.enabled });
  };

  const filteredConditions = conditions.filter((c) => c.category === activeCategory);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          条件管理
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => evaluate()}
            disabled={evaluating}
            startIcon={evaluating ? <CircularProgress size={16} /> : null}
          >
            {evaluating ? '筛选中...' : '手动筛选'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              if (confirm('确认恢复默认预设条件？当前自定义条件将保留。')) {
                resetDefaults();
              }
            }}
          >
            恢复默认预设
          </Button>
          <Button variant="contained" onClick={handleAdd}>
            新增条件
          </Button>
        </Box>
      </Box>

      {/* 自动交易控制面板 */}
      <Paper sx={{ p: 2, mb: 2, border: autoTradeStatus?.enabled ? '1px solid' : '1px dashed', borderColor: autoTradeStatus?.enabled ? 'success.main' : 'grey.300', bgcolor: autoTradeStatus?.enabled ? 'rgba(76, 175, 80, 0.05)' : 'inherit' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <AutoAwesomeIcon color={autoTradeStatus?.enabled ? 'success' : 'disabled'} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            自动模拟交易
          </Typography>
          <Switch
            checked={autoTradeStatus?.enabled ?? false}
            onChange={handleToggleAutoTrade}
            disabled={autoTradeLoading}
          />
          <Chip
            size="small"
            label={autoTradeStatus?.enabled ? '已开启' : '已关闭'}
            color={autoTradeStatus?.enabled ? 'success' : 'default'}
            variant={autoTradeStatus?.enabled ? 'filled' : 'outlined'}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
            <Typography variant="body2" color="text.secondary">每只最大持仓数:</Typography>
            <TextField
              size="small"
              type="number"
              value={maxStocksInput}
              onChange={(e) => setMaxStocksInput(e.target.value)}
              sx={{ width: 70 }}
              inputProps={{ min: 1, max: 50 }}
            />
            <Button size="small" variant="outlined" onClick={handleSaveMaxStocks}>保存</Button>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="立即执行卖出+记录盈亏（15:10等效）">
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={handleRunAutoTrade}
              disabled={autoTradeLoading}
              startIcon={autoTradeLoading ? <CircularProgress size={16} /> : null}
            >
              立即卖出
            </Button>
          </Tooltip>
        </Box>
        {autoTradeStatus?.lastRunResult && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            上次运行: {autoTradeStatus.lastRunDate} | 买入{autoTradeStatus.lastRunResult.bought}只 卖出{autoTradeStatus.lastRunResult.sold}只
            {autoTradeStatus.lastRunResult.paused ? ` (暂停: ${autoTradeStatus.lastRunResult.reason})` : ''}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          次日{autoTradeStatus?.buyTime || '10:00'}自动买入昨日候选股（实时价）→ 15:10自动卖出满足条件持仓→记录盈亏。交易时段每60秒检查：卖出条件（按当前价判断，首次满足阈值即按底线价卖出）+ 补仓条件（持仓较买入价跌≥5%即按底线价补仓一次，不受整体条件限制；补仓后较补仓价涨≥2.5%两笔全部清仓）。整体条件（下跌＞3500家/指数跌1%）只禁买入、不影响卖出。
        </Typography>
        {autoTradeResult && (
          <Alert severity={autoTradeResult.includes('失败') || autoTradeResult.includes('暂停') ? 'warning' : 'success'} sx={{ mt: 1 }}>
            {autoTradeResult}
          </Alert>
        )}
      </Paper>

      {/* AI 解析输入 */}
      <AiParseInput />

      {/* 筛选结果提示 */}
      {candidates.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'success.light', color: '#fff' }}>
          <Typography variant="body2">
            手动筛选完成：找到 {candidates.length} 只候选股，请到首页查看
          </Typography>
        </Paper>
      )}

      {/* 分类 Tab */}
      <Tabs
        value={activeCategory}
        onChange={(_e, val) => setActiveCategory(val)}
        sx={{ mb: 2 }}
      >
        <Tab label={CATEGORY_LABELS.buy} value="buy" />
        <Tab label={CATEGORY_LABELS.sell} value="sell" />
        <Tab label={CATEGORY_LABELS.global} value="global" />
      </Tabs>

      {/* 条件列表 */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ width: 60 }}>启用</TableCell>
              <TableCell sx={{ width: 120 }}>规则类型</TableCell>
              <TableCell>条件描述</TableCell>
              <TableCell sx={{ width: 80 }}>预设</TableCell>
              <TableCell sx={{ width: 100 }} align="center">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredConditions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    暂无{CATEGORY_LABELS[activeCategory]}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredConditions.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>
                    <Switch
                      size="small"
                      checked={c.enabled}
                      onChange={() => handleToggleEnabled(c)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={RULE_TYPE_LABELS[c.ruleType] || c.ruleType}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{c.description}</Typography>
                    {c.value && (
                      <Typography variant="caption" color="text.secondary">
                        {c.operator} {c.value}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {c.isPreset && <Chip size="small" label="预设" color="primary" variant="outlined" />}
                  </TableCell>
                  <TableCell align="center">
                    <Button size="small" onClick={() => handleEdit(c)}>编辑</Button>
                    <Button size="small" color="error" onClick={() => handleDelete(c.id!)}>
                      删除
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ConditionEditor
        open={editorOpen}
        condition={editingCondition}
        defaultCategory={activeCategory}
        onClose={() => setEditorOpen(false)}
        onSave={handleSave}
      />
    </Box>
  );
}
